import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Eye, PackageSearch, Search, Download } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminListOrders, adminGetOrder, adminUpdateOrderStatus } from "../../services/api";
import { formatINR } from "../../data/content";

const rupees = (paise) => formatINR((paise || 0) / 100);

const ORDER_STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  pending_payment: "bg-amber-100 text-amber-800",
  confirmed: "bg-indigo-100 text-indigo-800",
  paid: "bg-emerald-100 text-emerald-800",
  shipped: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-700",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid="order-status"
    className={`inline-block whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
      STATUS_STYLES[status] || "bg-brand-sand text-brand-charcoal"
    }`}
  >
    {(status || "unknown").replace(/_/g, " ")}
  </span>
);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso || "—";
  }
};

const OrderDetail = ({ order, onClose }) => {
  const c = order.customer || {};
  const a = c.address || {};
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail-overlay">
      <div className="absolute inset-0 bg-brand-charcoal/50" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-brand-cream shadow-[0_0_60px_-15px_rgba(28,25,23,0.5)]" data-testid="order-detail">
        <div className="sticky top-0 flex items-center justify-between border-b border-brand-sand bg-brand-surface px-6 py-5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-stone">Order</p>
            <p className="font-mono text-sm font-semibold text-brand-charcoal" data-testid="detail-order-id">
              {order.order_id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="order-detail-close"
            className="flex h-9 w-9 items-center justify-center border border-brand-sand text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-8 px-6 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <StatusBadge status={order.status} />
            <span className="text-xs text-brand-stone">{fmtDate(order.created_at)}</span>
          </div>

          <section>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-stone">Customer</p>
            <p className="mt-2 font-serif text-lg text-brand-charcoal">{c.name || "—"}</p>
            <p className="text-sm text-brand-stone">{c.email || "—"}</p>
            <p className="text-sm text-brand-stone">{c.phone || "—"}</p>
          </section>

          <section>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-stone">Shipping address</p>
            {a.line1 ? (
              <address className="mt-2 not-italic text-sm leading-relaxed text-brand-charcoal" data-testid="detail-address">
                {a.line1}
                {a.line2 ? <>, {a.line2}</> : null}
                <br />
                {a.city}, {a.state} {a.pincode}
                <br />
                {a.country || "India"}
              </address>
            ) : (
              <p className="mt-2 text-sm text-brand-stone">No address on file.</p>
            )}
          </section>

          <section>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-stone">Items</p>
            <div className="mt-3 divide-y divide-brand-sand border-y border-brand-sand">
              {(order.items || []).map((it, i) => (
                <div key={`${it.slug}-${i}`} className="flex items-start justify-between gap-4 py-3" data-testid="detail-item">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-brand-charcoal">{it.name}</p>
                    {it.variant && Object.keys(it.variant).length > 0 && (
                      <p className="mt-0.5 text-[11px] uppercase tracking-[0.1em] text-brand-stone">
                        {Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-brand-stone">Qty {it.qty} × {rupees(it.unit_price_paise)}</p>
                  </div>
                  <p className="shrink-0 font-serif text-sm text-brand-charcoal">{rupees(it.line_total_paise)}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between text-brand-stone">
              <span>Subtotal</span>
              <span className="text-brand-charcoal">{rupees(order.subtotal_paise)}</span>
            </div>
            <div className="flex items-baseline justify-between border-t border-brand-sand pt-2 text-brand-charcoal">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Total</span>
              <span className="font-serif text-xl" data-testid="detail-total">{rupees(order.total_paise)}</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default function AdminOrders() {
  const { user, ready } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const filtered = orders.filter((o) => {
    if (statusFilter !== "all" && (o.status || "") !== statusFilter) return false;
    if (dateFilter && !(o.created_at || "").startsWith(dateFilter)) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = `${o.order_id || ""} ${o.customer?.name || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    if (!filtered.length) return;
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["order_id", "customer", "status", "total_inr", "created_at"];
    const rows = filtered.map((o) =>
      [o.order_id, o.customer?.name, o.status, (o.total_paise || 0) / 100, o.created_at].map(esc).join(","),
    );
    const csv = [header.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!ready || user?.role !== "admin") return;
    setLoading(true);
    setError("");
    adminListOrders({ limit: 200 })
      .then((rows) => setOrders(Array.isArray(rows) ? rows : []))
      .catch((err) =>
        setError(err?.response?.status === 401 ? "Your session has expired. Please sign in again." : "Could not load orders."),
      )
      .finally(() => setLoading(false));
  }, [ready, user]);

  const openOrder = async (order) => {
    setOpening(true);
    try {
      const full = await adminGetOrder(order.order_id);
      setSelected(full);
    } catch {
      setSelected(order); // fall back to list row
    } finally {
      setOpening(false);
    }
  };

  const changeStatus = async (orderId, status) => {
    try {
      await adminUpdateOrderStatus(orderId, status);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, status } : o)));
      setSelected((s) => (s && s.order_id === orderId ? { ...s, status } : s));
      toast.success("Order status updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status.");
    }
  };

  return (
    <AdminLayout
      title="Orders"
      subtitle="Customer orders from checkout"
      actions={
        <button onClick={exportCsv} disabled={!filtered.length} data-testid="orders-export" className="inline-flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      }
    >
      <div data-testid="admin-orders-page">
        {error && (
          <p className="mb-6 text-sm text-red-600" data-testid="orders-error">{error}</p>
        )}

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-stone" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search order # or customer"
              data-testid="orders-search"
              className="w-full border border-brand-sand bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand-orange"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="orders-status-filter" className="border border-brand-sand bg-white px-3 py-2.5 text-sm capitalize outline-none focus:border-brand-orange">
            <option value="all">All statuses</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="pending_payment">pending_payment</option>
          </select>
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="orders-date-filter" className="border border-brand-sand bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-orange" />
          {(q || statusFilter !== "all" || dateFilter) && (
            <button type="button" onClick={() => { setQ(""); setStatusFilter("all"); setDateFilter(""); }} data-testid="orders-clear-filters" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-stone hover:text-brand-orange">Clear</button>
          )}
        </div>

        <div className="overflow-x-auto border border-brand-sand bg-brand-surface" data-testid="orders-table">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-brand-sand text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone">
                <th className="p-3">Order #</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Date</th>
                <th className="p-3">Items</th>
                <th className="p-3">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">View</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-brand-stone" data-testid="orders-loading">Loading orders…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-brand-stone" data-testid="orders-empty">
                    <PackageSearch className="mx-auto mb-3 h-8 w-8 text-brand-sand" />
                    No orders match. Orders placed at checkout will appear here.
                  </td>
                </tr>
              ) : (
                filtered.map((o) => (
                  <tr
                    key={o.order_id}
                    className="cursor-pointer border-b border-brand-sand/50 transition-colors hover:bg-brand-champagne/40"
                    data-testid={`order-row-${o.order_id}`}
                    onClick={() => openOrder(o)}
                  >
                    <td className="p-3 font-mono text-xs text-brand-charcoal">{o.order_id?.slice(0, 10)}</td>
                    <td className="p-3">
                      <p className="font-medium text-brand-charcoal">{o.customer?.name || "—"}</p>
                      <p className="text-xs text-brand-stone">{o.customer?.email || o.customer?.phone || ""}</p>
                    </td>
                    <td className="p-3 whitespace-nowrap text-brand-stone">{fmtDate(o.created_at)}</td>
                    <td className="p-3 text-brand-charcoal">{(o.items || []).reduce((n, it) => n + (it.qty || 0), 0)}</td>
                    <td className="p-3 font-serif text-brand-charcoal">{rupees(o.total_paise)}</td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={ORDER_STATUSES.includes(o.status) ? o.status : "pending"}
                        onChange={(e) => changeStatus(o.order_id, e.target.value)}
                        data-testid={`order-status-select-${o.order_id}`}
                        className="border border-brand-sand bg-white px-2 py-1.5 text-xs capitalize text-brand-charcoal outline-none focus:border-brand-orange"
                      >
                        {ORDER_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openOrder(o); }}
                        data-testid={`order-view-${o.order_id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-brand-orange hover:underline"
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {opening && !selected && (
          <p className="mt-4 text-xs text-brand-stone" data-testid="order-opening">Opening order…</p>
        )}
      </div>

      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} />}
    </AdminLayout>
  );
}
